/**
 * Money and quantity text, exactly as the server prints them
 * (internal/money/format.go, legacy go-prices): integers in minor units,
 * quantities in thousandths, never floats.
 *
 * The client does no money arithmetic — totals come from
 * `POST …/totals-preview` — it only parses what the user types and prints
 * what the server returns. Upstream candidate (ADMINER_PROPOSALS A1).
 */

export type Currency = "CZK" | "EUR";

interface Layout {
  decimal: string;
  thousands: string;
  prefix: string;
  suffix: string;
}

const LAYOUTS: Record<string, Layout> = {
  CZK: { decimal: ",", thousands: " ", prefix: "", suffix: " Kč" },
  EUR: { decimal: ".", thousands: ",", prefix: "€", suffix: "" },
};

function absDigits(v: number, min: number): string {
  return Math.abs(v).toString().padStart(min, "0");
}

/** The sign a price field carries as its affix: "Kč", "€", else the code. */
export function currencySign(currency: string): string {
  const l = LAYOUTS[currency];
  return l ? (l.prefix || l.suffix).trim() : currency;
}

/** "1 234,56 Kč", "€1,234.56", "-12,34 Kč" — byte for byte the server's. */
export function formatMoney(minor: number, currency: string): string {
  const l = LAYOUTS[currency] ?? { decimal: ".", thousands: ",", prefix: "", suffix: ` ${currency}` };
  const digits = absDigits(minor, 3);
  const whole = digits.slice(0, -2);
  const frac = digits.slice(-2);
  let grouped = "";
  for (let i = 0; i < whole.length; i++) {
    if (i > 0 && (whole.length - i) % 3 === 0) grouped += l.thousands;
    grouped += whole[i];
  }
  return `${minor < 0 ? "-" : ""}${l.prefix}${grouped}${l.decimal}${frac}${l.suffix}`;
}

/** "1,5", "0,001", "1000" — quantity in thousandths, trailing zeros dropped. */
export function formatQuantity(milli: number): string {
  const digits = absDigits(milli, 4);
  const s = `${digits.slice(0, -3)},${digits.slice(-3)}`.replace(/0+$/, "").replace(/,$/, "");
  return milli < 0 ? `-${s}` : s;
}

/**
 * The editable text of an integer in units of 10^-scale, every decimal
 * shown as the legacy DecimalNumberInput did: "1234,50" for 123450 at
 * scale 2, "1,000" for 1000 at scale 3. No grouping (the field is for
 * typing), comma decimal.
 */
export function decimalText(value: number | null, scale: number): string {
  if (value === null) return "";
  if (scale === 0) return String(value);
  const digits = absDigits(value, scale + 1);
  const s = `${digits.slice(0, -scale)},${digits.slice(-scale)}`;
  return value < 0 ? `-${s}` : s;
}

/**
 * Parses typed text into an integer in units of 10^-scale, exactly (string
 * arithmetic, no float). Accepts "1 234,5", "1234.50", "-3"; spaces and NBSP
 * group. Returns undefined for anything else, including more decimals than
 * the scale holds — the field says so instead of rounding behind the user's
 * back.
 */
export function parseDecimal(text: string, scale: number): number | null | undefined {
  const t = text.replace(/[\s ]/g, "");
  if (t === "") return null;
  const m = /^(-)?(\d*)(?:[.,](\d*))?$/.exec(t);
  if (!m || (m[2] === "" && (m[3] ?? "") === "")) return undefined;
  const frac = m[3] ?? "";
  if (frac.length > scale) return undefined;
  const n = Number((m[2] || "0") + frac.padEnd(scale, "0"));
  if (!Number.isSafeInteger(n)) return undefined;
  return m[1] ? -n : n;
}
